import { useState } from 'react';
import {
  DjangoListResponse,
  ItemList,
  ChargebeeSubscription,
  PlanPermission,
  EnrichedSubscription,
  PlanPermissionData,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { useQuery } from 'react-query';
import { validateRestrictions } from 'v2/utils/licensesUtils';
import { BulkInvitation } from 'v2/pages/Settings/MyTeam/BuyerMyTeam/InviteTeamMemberModal/InviteTeamMemberModal';

type PlanCounts = {
  [key: string]: number;
};
export interface ExtendedSubscription extends ChargebeeSubscription {
  permissions: PlanPermissionData[];
  selected: boolean;
  isDisabled: boolean;
  tooltip: string;
}

const transformSubscriptionsToOptions = (
  subscriptions: ExtendedSubscription[],
  addedSubscriptions: Record<string, number> = {}
) => {
  return subscriptions.map((subscription) => {
    let remaining = subscription.remaining;
    const subscriptionName = subscription.plan_fk.name.replaceAll('-', ' ');
    if (subscriptionName in addedSubscriptions) {
      remaining = subscription.remaining - addedSubscriptions[subscriptionName];
    }
    return {
      label: subscriptionName,
      value: {
        id: subscription.id,
        name: subscriptionName,
        isDisabled:
          (remaining === 0 && !subscription.selected) ||
          subscription.isDisabled,
        tooltip: subscription.tooltip,
      },
      isDisabled:
        (remaining === 0 && !subscription.selected) || subscription.isDisabled,
    };
  });
};

export const getOptions = (
  bulkInvitations: BulkInvitation[],
  ownedPlanPermissions: PlanPermission = {},
  currentSelection: ItemList,
  availableSubscriptions?: ChargebeeSubscription[]
) => {
  const allAddedLicenses = bulkInvitations.reduce<PlanCounts>(
    (acc, invitation) => {
      invitation.licenses?.forEach(({ value: { name } }) => {
        acc[name] = (acc[name] || 0) + 1;
      });
      return acc;
    },
    {}
  );
  const enrichedSubscriptions: EnrichedSubscription[] = [];
  availableSubscriptions?.forEach((subscription) => {
    const permissions = ownedPlanPermissions[subscription.plan_fk.name];
    const elementIndex = currentSelection.list.findIndex(
      (element) => element.id === subscription.id
    );
    const selected = elementIndex !== -1;
    const enrichedSubscription = {
      ...subscription,
      selected: selected,
      permissions: permissions || [],
    };
    enrichedSubscriptions.push(enrichedSubscription);
  });
  const extendedSubscriptions = enrichedSubscriptions.map((subscription) => {
    const [isDisabled, tooltip] = validateRestrictions(
      subscription,
      enrichedSubscriptions
    );
    return {
      ...subscription,
      isDisabled: isDisabled,
      tooltip: tooltip,
    };
  });

  const options = transformSubscriptionsToOptions(
    extendedSubscriptions || [],
    allAddedLicenses
  );
  return options;
};

export const useMultiSelectLicensesOptions = (
  bulkInvitations: BulkInvitation[]
) => {
  const [currentSelection, setCurrentSelection] = useState<ItemList>({
    is_all: false,
    list: [],
  });

  const { data: availableSubscriptions } = useQuery(
    ['Get subscriptions'],
    async () => {
      const response = await restFactory.get<
        DjangoListResponse<ChargebeeSubscription>
      >('/dj/api/v1/chargebee_subscription_multiuser/');
      return response;
    }
  );

  const { data: ownedPlanPermissions } = useQuery(
    ['Get plan permissions by user'],
    async () => {
      const response = await restFactory.get<PlanPermission>(
        `/dj/api/v1/chargebee_subscription_multiuser/get_plan_permissions/`
      );
      return response;
    }
  );
  const options = getOptions(
    bulkInvitations,
    ownedPlanPermissions,
    currentSelection,
    availableSubscriptions?.results
  );
  return {
    options,
    currentSelection,
    setCurrentSelection,
  };
};
